import React, { useMemo, useEffect, useState, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Card,
  Box,
  Button,
  Grid,
  CircularProgress
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { useFormik } from 'formik';
import _ from 'lodash';
import { useNavigate } from 'react-router';
import { BsFillTriangleFill } from 'react-icons/bs';
import useApi from 'src/api/api';
import { useBackDrop } from 'src/provider/BackdropProvider';
import { useLocale } from 'src/provider/LocaleProvider';
import { useSnack } from 'src/provider/SnackbarProvider';
import LayoutContainer from 'src/layout/LayoutContainer';
import EditDialog from 'src/components/EditDialog';
import utils from 'src/utils/utils';
import classNames from 'classnames';

// Normalize string for comparisons: handle null/undefined, trim whitespace and normalize unicode
const normalizeStr = (s) => {
  const raw = s === undefined || s === null ? '' : s.toString();
  const trimmed = raw.trim();
  const normalized = typeof trimmed.normalize === 'function' ? trimmed.normalize('NFC') : trimmed;
  
  // Handle percentage values: "11.30%" should equal "11.3%"
  // Also handle decimal numbers: "1.50" should equal "1.5"
  const percentMatch = normalized.match(/^(-?\d+\.?\d*)%$/);
  if (percentMatch) {
    // Parse the number, then add % back - this removes trailing zeros
    const num = parseFloat(percentMatch[1]);
    return isNaN(num) ? normalized : `${num}%`;
  }
  
  // Handle plain decimal numbers: "1.50" should equal "1.5"
  const numberMatch = normalized.match(/^-?\d+\.\d+$/);
  if (numberMatch) {
    const num = parseFloat(normalized);
    return isNaN(num) ? normalized : num.toString();
  }
  
  return normalized;
};

// Validation function for question data
const validateQuestion = (question, t, availableGames = []) => {
  const errors = {};

  if (!question.gameId) {
    errors.gameId = t('game_id_required');
  } else {
    // Check if gameId exists in available games
    const gameExists = availableGames.find(
      (game) => game._id === question.gameId
    );
    if (!gameExists) {
      errors.gameId = t('invalid_game_id');
    }
  }

  if (!question.text) {
    errors.text = t('question_text_required');
  }

  // Always validate multiple choice options since all questions are MC
  if (!question.optionA) {
    errors.optionA = t('option_required');
  }
  if (!question.optionB) {
    errors.optionB = t('option_required');
  }
  
  // Validate correct answer format
  if (!question.correctAnswer || question.correctAnswer.trim() === '') {
    errors.correctAnswer = t('correct_answer_required');
  } else {
    // Check if correctAnswer contains only valid letters (A, B, C, D) and commas
    const cleanAnswer = question.correctAnswer.replace(/\s/g, ''); // Remove all whitespace
    const validPattern = /^[A-D](,[A-D])*$/; // Pattern: A or A,B or A,B,C etc.
    
    if (!validPattern.test(cleanAnswer)) {
      errors.correctAnswer = t('correct_answer_invalid_format');
    } else {
      // Also check each letter is valid
      const letters = cleanAnswer.split(',');
      if (!letters.every((ans) => ['A', 'B', 'C', 'D'].includes(ans))) {
        errors.correctAnswer = t('correct_answer_invalid_format');
      }
    }
  }

  return errors;
};

// Helper function to format game display - prioritize game name for better readability
const formatGameDisplay = (gameId, availableGames = [], t) => {
  if (!gameId) return '';

  const game = availableGames.find((g) => g._id === gameId);
  if (game) {
    const gameName =
      game.name?.en || game.name?.zh || game.name || t('unknown');
    return gameName;
  }

  // Return the actual gameId value instead of "invalid_game_id" text
  return gameId;
};

// Helper function to get game ID for display in smaller text
const getGameId = (gameId) => {
  return gameId || '';
};

const useStyles = makeStyles({
  table: {
    '& .MuiTableRow-root': {
      height: '50px'
    },
    '& .MuiTableCell-root': {
      whiteSpace: 'nowrap',
      textAlign: 'center',
      border: '1px solid rgba(224, 224, 224, 1)',
      padding: '8px 4px',
      fontSize: '0.875rem',
      '&:first-child': {
        paddingLeft: '8px'
      },
      '&:last-child': {
        paddingRight: '8px'
      },
      '&.game-cell': {
        whiteSpace: 'normal', // Allow wrapping for game ID cells
        textAlign: 'left' // Left align for better readability
      }
    },
    '& .borderLeft': {
      borderLeft: '2px solid rgba(224, 224, 224, 0.8)'
    },
    '& .MuiTableSortLabel-icon': {
      opacity: 0.5
    },
    '& .Mui-active .MuiTableSortLabel-icon': {
      opacity: 1,
      color: 'limegreen'
    },
    '& .row-existing': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
      '&:hover': {
        backgroundColor: 'rgba(245, 245, 245, 1)'
      }
    },
    '& .row-new': {
      backgroundColor: 'rgba(50, 255, 50, 0.2) !important',
      '&:hover': {
        backgroundColor: 'rgba(50, 255, 50, 0.3) !important'
      }
    },
    '& .row-update': {
      backgroundColor: 'rgba(255, 255, 50, 0.2) !important',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 50, 0.3) !important'
      }
    },
    '& .cell-update': {
      backgroundColor: 'rgba(255, 193, 7, 0.7) !important', // Stronger amber background for changed cells - use !important to override row color
      fontWeight: 'bold',
      border: '3px solid rgba(255, 165, 0, 1) !important', // Thicker orange border for maximum visibility
      position: 'relative',
      zIndex: 10 // High z-index to ensure cell updates show above row background
    },
    '& .error': {
      backgroundColor: 'rgba(255, 50, 50,0.2) !important',
      '&:hover': {
        backgroundColor: 'rgba(255, 50, 50,0.3) !important'
      }
    }
  },
  toolbar: {
    display: 'flex',
    '& .left': {
      display: 'flex',
      margin: '-8px',
      width: 'calc(100% - 16px)',
      flexWrap: 'wrap',
      '& .item': {
        display: 'flex',
        alignItems: 'center',
        padding: '8px',
        whiteSpace: 'nowrap'
      }
    },
    '& .right': {
      display: 'flex',
      margin: '-8px',
      width: 'calc(100% - 16px)',
      flexWrap: 'wrap',
      '& .item': {
        display: 'flex',
        alignItems: 'center',
        padding: '8px',
        whiteSpace: 'nowrap'
      }
    }
  }
});

const GameQuestionUploadTable = ({
  existingQuestions = [],
  rows = [],
  loading = false,
  _orderBy = 'index',
  _order = 'asc',
  rowsPerPage = 10,
  rowsPerPageOptions = [5, 10, 25],
  rowsRef,
  validateQuestion,
  availableGames = [],
  ...rest
}) => {
  const { t } = useLocale();
  const classes = useStyles();

  const [order, setOrder] = useState(_order);
  const [orderBy, setOrderBy] = useState(_orderBy);

  const sorted = _.orderBy(
    _.map(rows, (r, index) => ({ ...r, index: index + 1 })),
    [orderBy],
    [order]
  );

  const handleSort = (path) => {
    if (orderBy === path) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(path);
      setOrder('asc');
    }
  };

  const headCell = (label, path, sort = true) => {
    if (sort) {
      return (
        <TableSortLabel
          active={orderBy === path}
          direction={orderBy === path ? order : 'asc'}
          onClick={(e) => handleSort(path)}
          hideSortIcon={false}
        >
          {label}
        </TableSortLabel>
      );
    } else return label;
  };

  return (
    <Card {...rest}>
      <Box sx={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '500px' }}>
        <Table
          size="small"
          className={classNames(classes.table)}
          sx={{ minWidth: 820 }}
        >
          <TableHead>
            <TableRow sx={{ height: '30px' }}>
              <TableCell width="60px" sx={{ minWidth: '60px' }}>
                {headCell(t('index'), 'index')}
              </TableCell>
              <TableCell width="80px" sx={{ minWidth: '80px' }}>
                {headCell(t('question_id'), 'id', false)}
              </TableCell>
              <TableCell className="borderLeft" sx={{ minWidth: '200px' }}>
                {headCell(t('game_id'), 'gameId')}
              </TableCell>
              <TableCell sx={{ minWidth: '200px' }}>
                {headCell(t('question_text'), 'text')}
              </TableCell>
              <TableCell sx={{ minWidth: '150px' }}>
                {headCell(t('option_a'), 'optionA')}
              </TableCell>
              <TableCell sx={{ minWidth: '150px' }}>
                {headCell(t('option_b'), 'optionB')}
              </TableCell>
              <TableCell sx={{ minWidth: '150px' }}>
                {headCell(t('option_c'), 'optionC')}
              </TableCell>
              <TableCell sx={{ minWidth: '150px' }}>
                {headCell(t('option_d'), 'optionD')}
              </TableCell>
              <TableCell sx={{ minWidth: '100px' }}>
                {headCell(t('correct_answer'), 'correctAnswer')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center" style={{ padding: '40px' }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : _.map(sorted, (row, index) => {
              // Use validation function with availableGames instead of formik errors
              const errors = validateQuestion
                ? validateQuestion(row, t, availableGames)
                : {};
              const isError = Object.keys(errors).length > 0;

              const rowClasses = {
                'row-existing': row.status === 'existing' && !isError,
                'row-new': row.status === 'new' && !isError,
                'row-update': false, // Don't color entire row for updates, only individual cells
                error: isError
              };

              // Find existing question for field comparison (like GameUpload does)
              const existingQuestion = row.id 
                ? existingQuestions.find(q => q._id === row.id || q.id === row.id)
                : null;

              const isFieldChanged = (field, newValue) => {
                if (!existingQuestion || row.status !== 'update') return false;
                
                let oldValue;
                if (field === 'gameId') {
                  oldValue = existingQuestion.game || '';
                } else if (field === 'text') {
                  oldValue = existingQuestion.text || '';
                } else if (field === 'optionA') {
                  oldValue = (existingQuestion.options || [])[0]?.text || '';
                } else if (field === 'optionB') {
                  oldValue = (existingQuestion.options || [])[1]?.text || '';
                } else if (field === 'optionC') {
                  oldValue = (existingQuestion.options || [])[2]?.text || '';
                } else if (field === 'optionD') {
                  oldValue = (existingQuestion.options || [])[3]?.text || '';
                } else if (field === 'correctAnswer') {
                  // Get correct answers from existing question
                  const correctAnswers = [];
                  for (let i = 0; i < (existingQuestion.options || []).length; i++) {
                    if (existingQuestion.options[i]?.isCorrect || existingQuestion.options[i]?.isAns) {
                      correctAnswers.push(String.fromCharCode(65 + i));
                    }
                  }
                  oldValue = correctAnswers.length > 0 ? correctAnswers.sort().join(',') : 'A';
                  
                  // Compare sorted correct answers
                  const oldSorted = (oldValue || 'A').split(',').sort().join(',');
                  const newSorted = (newValue || 'A').split(',').sort().join(',');
                  return oldSorted !== newSorted;
                }
                
                // For other fields, compare as trimmed strings
                const oldStr = (oldValue || '').toString().trim();
                const newStr = (newValue || '').toString().trim();
                return oldStr !== newStr;
              };

              return (
                <>
                  <TableRow key={index} className={classNames(rowClasses)}>
                    <TableCell>{row.index}</TableCell>
                    <TableCell>{row.id || ''}</TableCell>
                    <TableCell
                      className={classNames({ 
                        'cell-update': isFieldChanged('gameId', row.gameId),
                        'game-cell': true
                      })}
                      sx={{ maxWidth: '200px', overflow: 'hidden' }}
                    >
                      <div>
                        <div
                          style={{
                            fontWeight: 'bold',
                            fontSize: '0.875rem',  
                          }}
                        >
                          {formatGameDisplay(row.gameId, availableGames, t)}
                        </div>
                        {!isError && (
                          <div
                            style={{
                              fontSize: '0.75rem',
                              color: '#666',
                              marginTop: '2px'
                            }}
                          >
                            {getGameId(row.gameId)}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell
                      className={classNames({ 
                        'cell-update': isFieldChanged('text', row.text)
                      })}
                      sx={{
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {row.text || ''}
                    </TableCell>
                    <TableCell
                      className={classNames({ 
                        'cell-update': isFieldChanged('optionA', row.optionA)
                      })}
                      sx={{
                        maxWidth: '150px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {row.optionA || ''}
                    </TableCell>
                    <TableCell
                      className={classNames({ 
                        'cell-update': isFieldChanged('optionB', row.optionB)
                      })}
                      sx={{
                        maxWidth: '150px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {row.optionB || ''}
                    </TableCell>
                    <TableCell
                      className={classNames({ 
                        'cell-update': isFieldChanged('optionC', row.optionC)
                      })}
                      sx={{
                        maxWidth: '150px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {row.optionC || ''}
                    </TableCell>
                    <TableCell
                      className={classNames({ 
                        'cell-update': isFieldChanged('optionD', row.optionD)
                      })}
                      sx={{
                        maxWidth: '150px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {row.optionD || ''}
                    </TableCell>
                    <TableCell className={classNames({ 
                      'cell-update': isFieldChanged('correctAnswer', row.correctAnswer)
                    })}>
                      {row.correctAnswer || ''}
                    </TableCell>
                  </TableRow>
                  {Object.keys(errors).length > 0 && (
                    <TableRow>
                      <TableCell colSpan={99} sx={{ px: 1 }}>
                        <Box display="flex" flexDirection="row">
                          <BsFillTriangleFill
                            style={{
                              height: '16px',
                              width: '16px',
                              color: 'red'
                            }}
                          />
                          <Box sx={{ color: 'red', ml: 1, textAlign: 'left' }}>
                            {Object.keys(errors).map((e) => {
                              return (
                                <p
                                  key={e}
                                  style={{ marginTop: 0, marginBottom: 4 }}
                                >
                                  {`${t(e)} : ${errors[e]}`}
                                </p>
                              );
                            })}
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              );
            })}
            {!loading && _.size(sorted) === 0 && (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography variant="h5">{t('no_records')}</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Card>
  );
};

const GameQuestionUpload = () => {
  const api = useApi();
  const nav = useNavigate();
  const { t } = useLocale();
  const backDrop = useBackDrop();
  const snack = useSnack();

  const [existingQuestions, setExistingQuestions] = useState([]);
  const [newQuestionList, setNewQuestionList] = useState([]);
  const [open, setOpen] = useState(false);
  const [availableGames, setAvailableGames] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [hasUploadedFile, setHasUploadedFile] = useState(false);
  const [isLoadingSourceData, setIsLoadingSourceData] = useState(true);
  const [sourceDataError, setSourceDataError] = useState(null);
  const [pendingCsvFile, setPendingCsvFile] = useState(null);
  const hasLoadedData = useRef(false);

  const formik = useFormik({
    initialValues: {},
    onSubmit: async (values, helper) => {
      try {
        backDrop.open();

        // Prepare bulk update data for all questions that need to be created or updated
        const bulkData = [];

        for (const question of newQuestionList) {
          if (question.status === 'new' || question.status === 'update') {
            // Ensure correctAnswer is a string and handle undefined
            const correctAnswerStr = (question.correctAnswer || '').toString();
            const correctAnswersArr = correctAnswerStr.split(',').map(a => a.trim());

            // Prepare options array
            const options = [
              {
                text: question.optionA,
                isAns: correctAnswersArr.includes('A')
              },
              {
                text: question.optionB,
                isAns: correctAnswersArr.includes('B')
              },
              {
                text: question.optionC || '',
                isAns: correctAnswersArr.includes('C')
              },
              {
                text: question.optionD || '',
                isAns: correctAnswersArr.includes('D')
              }
            ].filter((opt) => opt.text && opt.text.trim());

            // Prepare question data for bulk update
            const questionData = {
              game: question.gameId,
              text: question.text,
              options: options
            };

            // Add ID only for updates, not for new questions
            // If question.id is empty or undefined, it will create a new question
            // If question.id has a value, it will update the existing question
            if (question.status === 'update' && question.id) {
              questionData.id = question.id;
            }

            bulkData.push(questionData);
          }
        }

        // Submit all questions in bulk if there are any changes
        if (bulkData.length > 0) {
          await api.bulkUpdateQuestions(bulkData);
        }

        snack.open(t('upload_success'));
        nav('/admin/games');
      } catch (error) {
        console.error('Upload error:', error);
        snack.error(t('upload_fail'));
      } finally {
        helper.setSubmitting(false);
        backDrop.close();
        setOpen(false);
      }
    }
  });

  useEffect(() => {
    // Prevent multiple API calls
    if (hasLoadedData.current) return;
    hasLoadedData.current = true;

    // Load existing games and questions
    const loadData = async () => {
      setIsLoadingSourceData(true);
      setSourceDataError(null);
      try {
        console.log('Loading games and questions...');

        // Load games and all questions in parallel
        const [gamesRes, questionsRes] = await Promise.all([
          api.getGameList(),
          api.getAllGameQuestions()
        ]);

        const games = gamesRes.data || [];
        setAvailableGames(games);

        const allQuestions = questionsRes.data || [];
        setExistingQuestions(allQuestions);

        setIsLoadingSourceData(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setSourceDataError(error.message || 'Failed to load source data');
        setIsLoadingSourceData(false);
        hasLoadedData.current = false; // Reset on error so it can retry
      }
    };

    loadData();
  }, [api]);

  // Process pending CSV file when source data becomes ready
  useEffect(() => {
    if (!isLoadingSourceData && pendingCsvFile && !sourceDataError) {
      const processCsv = async () => {
        const file = pendingCsvFile;
        setPendingCsvFile(null);
        
        try {
          var csv = await utils.readFileAsText(file);

          var arr = await utils.csv2JsonArray(csv, {
            question_id: 'id',
            id: 'id',
            Game_ID: 'gameId',
            Question_Text: 'text',
            Option_A: 'optionA',
            Option_B: 'optionB',
            Option_C: 'optionC',
            Option_D: 'optionD',
            Correct_Answer: 'correctAnswer'
          });


          // Filter out only completely empty rows
          const validCsvData = _.filter(arr, (item) => {
            return (
              (item.text && item.text.trim()) ||
              (item.gameId && item.gameId.trim()) ||
              (item.id && item.id.trim()) ||
              (item.optionA && item.optionA.trim()) ||
              (item.optionB && item.optionB.trim())
            );
          });

          // Process the data - reuse the same logic from handleCsv
          const processedData = validCsvData.map((csvRow) => {
            const optionData = {
              optionA: csvRow.optionA || '',
              optionB: csvRow.optionB || '',
              optionC: csvRow.optionC || '',
              optionD: csvRow.optionD || '',
              correctAnswer: csvRow.correctAnswer || 'A'
            };

            const csvRowData = {
              id: csvRow.id || '',
              gameId: csvRow.gameId || '',
              text: csvRow.text,
              optionA: optionData.optionA,
              optionB: optionData.optionB,
              optionC: optionData.optionC,
              optionD: optionData.optionD,
              correctAnswer: optionData.correctAnswer
            };

            let existingQuestion = null;
            let hasInvalidData = false;
            
            if (csvRow.id && csvRow.id.trim()) {
              const csvId = csvRow.id.trim();
              existingQuestion = existingQuestions.find(
                (q) => {
                  const match = q._id === csvId || q.id === csvId || 
                               (q._id && q._id.toString() === csvId) ||
                               (q.id && q.id.toString() === csvId);
                  return match;
                }
              );
              
              if (!existingQuestion) {
                hasInvalidData = true;
              }
            } else {
              const hasRequiredFields = 
                csvRowData.text && csvRowData.text.trim() && 
                csvRowData.gameId && csvRowData.gameId.trim();
              
              if (!hasRequiredFields) {
                hasInvalidData = true;
              }
              
              const hasMinimumOptions = 
                (csvRowData.optionA && csvRowData.optionA.trim()) || 
                (csvRowData.optionB && csvRowData.optionB.trim());
              
              if (!hasMinimumOptions) {
                hasInvalidData = true;
              }
            }

            let status = 'new';
            if (existingQuestion) {
              const existingOptions = existingQuestion.options || [];

              let existingCorrectAnswers = [];
              for (let i = 0; i < existingOptions.length; i++) {
                if (existingOptions[i]?.isCorrect || existingOptions[i]?.isAns) {
                  existingCorrectAnswers.push(String.fromCharCode(65 + i));
                }
              }
              let existingCorrectAnswer =
                existingCorrectAnswers.length > 0
                  ? existingCorrectAnswers.sort().join(',')
                  : 'A';

              const existingData = {
                id: existingQuestion._id || existingQuestion.id,
                gameId: existingQuestion.game || '',
                text: existingQuestion.text || '',
                optionA: existingOptions[0]?.text || '',
                optionB: existingOptions[1]?.text || '',
                optionC: existingOptions[2]?.text || '',
                optionD: existingOptions[3]?.text || '',
                correctAnswer: existingCorrectAnswer
              };

              const gameIdChanged = normalizeStr(existingData.gameId) !== normalizeStr(csvRowData.gameId);
              const textChanged = normalizeStr(existingData.text) !== normalizeStr(csvRowData.text);
              const optionAChanged = normalizeStr(existingData.optionA) !== normalizeStr(csvRowData.optionA);
              const optionBChanged = normalizeStr(existingData.optionB) !== normalizeStr(csvRowData.optionB);
              const optionCChanged = normalizeStr(existingData.optionC) !== normalizeStr(csvRowData.optionC);
              const optionDChanged = normalizeStr(existingData.optionD) !== normalizeStr(csvRowData.optionD);
              const correctAnswerChanged = normalizeStr(existingData.correctAnswer.split(',').sort().join(',')) !== normalizeStr(csvRowData.correctAnswer.split(',').sort().join(','));
              
              const hasChanges = gameIdChanged || textChanged || optionAChanged || optionBChanged || optionCChanged || optionDChanged || correctAnswerChanged;

              status = hasChanges ? 'update' : 'existing';
            }

            if (hasInvalidData) {
              status = 'error';
            }

            return {
              ...csvRowData,
              status: status
            };
          });

          const existingNotInCsv = existingQuestions
            .filter((existingQuestion) => {
              return !validCsvData.find((csvRow) => {
                if (csvRow.id && csvRow.id.trim()) {
                  return existingQuestion._id === csvRow.id || existingQuestion.id === csvRow.id;
                }
                return false;
              });
            })
            .map((question) => {
              const options = question.options || [];

              let correctAnswers = [];
              for (let i = 0; i < options.length; i++) {
                if (options[i]?.isCorrect || options[i]?.isAns) {
                  correctAnswers.push(String.fromCharCode(65 + i));
                }
              }
              let correctAnswer =
                correctAnswers.length > 0 ? correctAnswers.sort().join(',') : 'A';

              return {
                id: question._id || question.id,
                gameId: question.game || '',
                text: question.text || '',
                optionA: options[0]?.text || '',
                optionB: options[1]?.text || '',
                optionC: options[2]?.text || '',
                optionD: options[3]?.text || '',
                correctAnswer: correctAnswer,
                status: 'existing'
              };
            });

          const combinedData = [...existingNotInCsv, ...processedData];
          
          const filteredData = combinedData.filter(item => {
            if (item.status === 'new' || item.status === 'update') {
              return true;
            }
            const errors = validateQuestion(item, t, availableGames);
            return Object.keys(errors).length > 0;
          });

          setNewQuestionList(filteredData);
          setIsLoadingData(false);
        } catch (error) {
          console.error('Error processing pending CSV:', error);
          setIsLoadingData(false);
        }
      };
      
      processCsv();
    }
  }, [isLoadingSourceData, pendingCsvFile, sourceDataError, existingQuestions, availableGames, t]);

  // Remove the problematic useEffect that was causing infinite loops
  // The form validation will work directly with newQuestionList state

  const handleConfirm = () => {
    setOpen(true);
  };

  const handleExport = () => {
    utils.exportCsv(
      [],
      [
        { label: 'Question_id', value: 'question_id' },
        { label: 'Game_ID', value: 'game_id' },
        { label: 'Question_Text', value: 'question_text' },
        { label: 'Option_A', value: 'option_a' },
        { label: 'Option_B', value: 'option_b' },
        { label: 'Option_C', value: 'option_c' },
        { label: 'Option_D', value: 'option_d' },
        { label: 'Correct_Answer', value: 'correct_answer' }
      ],
      'questions_template'
    );
  };

  const handleExportCurrentQuestions = () => {
    // Export current questions with their actual data - only use separate option columns
    const exportData = existingQuestions.map((question) => {
      const options = question.options || [];
      
      // Find all correct answers
      let correctAnswers = [];
      for (let i = 0; i < options.length; i++) {
        if (options[i]?.isCorrect || options[i]?.isAns) {
          correctAnswers.push(String.fromCharCode(65 + i)); // A=65, B=66, C=67, D=68
        }
      }

      return {
        question_id: question._id || question.id || '',
        game_id: question.game || '',
        question_text: question.text || '',
        option_a: options[0]?.text || '',
        option_b: options[1]?.text || '',
        option_c: options[2]?.text || '',
        option_d: options[3]?.text || '',
        correct_answer: correctAnswers.join(',')
      };
    });

    utils.exportCsv(
      exportData,
      [
        { label: 'Question_id', value: 'question_id' },
        { label: 'Game_ID', value: 'game_id' },
        { label: 'Question_Text', value: 'question_text' },
        { label: 'Option_A', value: 'option_a' },
        { label: 'Option_B', value: 'option_b' },
        { label: 'Option_C', value: 'option_c' },
        { label: 'Option_D', value: 'option_d' },
        { label: 'Correct_Answer', value: 'correct_answer' }
      ],
      'questions_data'
    );
  };

  const handleCsv = async (f) => {
    setIsLoadingData(true);
    
    if (isLoadingSourceData) {
      setPendingCsvFile(f);
      setHasUploadedFile(true);
      return;
    }
    
    try {
      console.log('Starting CSV import...');
      var csv = await utils.readFileAsText(f);
      console.log('CSV text loaded, length:', csv.length);

      var arr = await utils.csv2JsonArray(csv, {
        Question_id: 'id',
        id: 'id',
        Game_ID: 'gameId',
        Question_Text: 'text',
        Option_A: 'optionA',
        Option_B: 'optionB',
        Option_C: 'optionC',
        Option_D: 'optionD',
        Correct_Answer: 'correctAnswer'
      });

      // Filter out only completely empty rows - keep rows with any data for validation
      const validCsvData = _.filter(arr, (item) => {
        // Keep row if it has any meaningful data in key fields
        return (
          (item.text && item.text.trim()) ||
          (item.gameId && item.gameId.trim()) ||
          (item.id && item.id.trim()) ||
          (item.optionA && item.optionA.trim()) ||
          (item.optionB && item.optionB.trim())
        );
      });

      // Process the data and assign status
      const processedData = validCsvData.map((csvRow) => {

        // Use separate columns directly - this is now the only supported format
        const optionData = {
          optionA: csvRow.optionA || '',
          optionB: csvRow.optionB || '',
          optionC: csvRow.optionC || '',
          optionD: csvRow.optionD || '',
          correctAnswer: csvRow.correctAnswer || 'A'
        };

        // Prepare the CSV row data for comparison
        const csvRowData = {
          id: csvRow.id || '', // Only use CSV ID, don't inherit from existing question for new entries
          gameId: csvRow.gameId || '', // Only use gameId from CSV, do not fallback to id
          text: csvRow.text,
          optionA: optionData.optionA,
          optionB: optionData.optionB,
          optionC: optionData.optionC,
          optionD: optionData.optionD,
          correctAnswer: optionData.correctAnswer
        };

        // Check if question already exists and validate
        let existingQuestion = null;
        let hasInvalidData = false;
        
        if (csvRow.id && csvRow.id.trim()) {
          // IF question_id is provided → MUST exist in database
          const csvId = csvRow.id.trim();
          existingQuestion = existingQuestions.find(
            (q) => {
              const match = q._id === csvId || q.id === csvId || 
                           (q._id && q._id.toString() === csvId) ||
                           (q.id && q.id.toString() === csvId);
              return match;
            }
          );
          
          if (!existingQuestion) {
            // Question ID provided but not found in database → INVALID
            hasInvalidData = true;
            console.log('Invalid question ID:', csvRow.id);
          } else {
            // Found existing question - log comparison AFTER parsing
            console.log('Found existing question - comparing with CSV:', {
              id: csvRow.id,
              text: csvRow.text?.substring(0, 30),
              csvOptions: {
                A: csvRowData.optionA,
                B: csvRowData.optionB,
                C: csvRowData.optionC,
                D: csvRowData.optionD
              },
              existingOptions: (existingQuestion.options || []).map(o => o.text)
            });
          }
        } else {
          const hasRequiredFields = 
            csvRowData.text && csvRowData.text.trim() && 
            csvRowData.gameId && csvRowData.gameId.trim();
          
          if (!hasRequiredFields) {
            hasInvalidData = true;
          }
          
          // Check for valid options - at least optionA and optionB should be present
          const hasMinimumOptions = 
            (csvRowData.optionA && csvRowData.optionA.trim()) || 
            (csvRowData.optionB && csvRowData.optionB.trim());
          
          if (!hasMinimumOptions) {
            hasInvalidData = true;
          }
        }

        // Determine status by comparing with existing question
        let status = 'new';
        if (existingQuestion) {
          // Convert existing question to same format for comparison
          const existingOptions = existingQuestion.options || [];

          // Find all correct answers by checking which options are marked as correct
          let existingCorrectAnswers = [];
          for (let i = 0; i < existingOptions.length; i++) {
            if (existingOptions[i]?.isCorrect || existingOptions[i]?.isAns) {
              existingCorrectAnswers.push(String.fromCharCode(65 + i)); // A=65, B=66, C=67, D=68
            }
          }
          // Sort the correct answers for consistent comparison
          let existingCorrectAnswer =
            existingCorrectAnswers.length > 0
              ? existingCorrectAnswers.sort().join(',')
              : 'A';

          const existingData = {
            id: existingQuestion._id || existingQuestion.id,
            gameId: existingQuestion.game || '',
            text: existingQuestion.text || '',
            optionA: existingOptions[0]?.text || '',
            optionB: existingOptions[1]?.text || '',
            optionC: existingOptions[2]?.text || '',
            optionD: existingOptions[3]?.text || '',
            correctAnswer: existingCorrectAnswer
          };

          // Compare all relevant fields to determine if there are actual changes
          const gameIdChanged = normalizeStr(existingData.gameId) !== normalizeStr(csvRowData.gameId);
          const textChanged = normalizeStr(existingData.text) !== normalizeStr(csvRowData.text);
          const optionAChanged = normalizeStr(existingData.optionA) !== normalizeStr(csvRowData.optionA);
          const optionBChanged = normalizeStr(existingData.optionB) !== normalizeStr(csvRowData.optionB);
          const optionCChanged = normalizeStr(existingData.optionC) !== normalizeStr(csvRowData.optionC);
          const optionDChanged = normalizeStr(existingData.optionD) !== normalizeStr(csvRowData.optionD);
          const correctAnswerChanged = normalizeStr(existingData.correctAnswer.split(',').sort().join(',')) !== normalizeStr(csvRowData.correctAnswer.split(',').sort().join(','));
          
          const hasChanges = gameIdChanged || textChanged || optionAChanged || optionBChanged || optionCChanged || optionDChanged || correctAnswerChanged;

          status = hasChanges ? 'update' : 'existing';
        }

        // Mark as error if validation fails
        if (hasInvalidData) {
          status = 'error';
        }

        return {
          ...csvRowData,
          status: status
        };
      });

      console.log('Processed CSV data summary:', {
        total: processedData.length,
        statuses: _.countBy(processedData, 'status'),
        firstUpdateRow: processedData.find(d => d.status === 'update'),
        newQuestions: processedData
          .filter((d) => d.status === 'new')
          .map((d) => ({
            id: d.id,
            text: d.text?.substring(0, 30) + '...'
          }))
      });

      // Combine existing data with new imported data
      const existingNotInCsv = existingQuestions
        .filter((existingQuestion) => {
          // Exclude questions that are being updated in the CSV (only match by ID)
          return !validCsvData.find((csvRow) => {
            if (csvRow.id && csvRow.id.trim()) {
              // Only match by ID - if CSV has no ID, it's a new question (don't exclude existing)
              return existingQuestion._id === csvRow.id || existingQuestion.id === csvRow.id;
            }
            return false;
          });
        })
        .map((question) => {
          const options = question.options || [];

          // Find all correct answers by checking which options are marked as correct
          let correctAnswers = [];
          for (let i = 0; i < options.length; i++) {
            if (options[i]?.isCorrect || options[i]?.isAns) {
              correctAnswers.push(String.fromCharCode(65 + i)); // A=65, B=66, C=67, D=68
            }
          }
          // Sort the correct answers for consistent comparison
          let correctAnswer =
            correctAnswers.length > 0 ? correctAnswers.sort().join(',') : 'A';

          return {
            id: question._id || question.id,
            gameId: question.game || '',
            text: question.text || '',
            optionA: options[0]?.text || '',
            optionB: options[1]?.text || '',
            optionC: options[2]?.text || '',
            optionD: options[3]?.text || '',
            correctAnswer: correctAnswer,
            status: 'existing'
          };
        });

      const combinedData = [...existingNotInCsv, ...processedData];
      
      const filteredData = combinedData.filter(item => {
        if (item.status === 'new' || item.status === 'update') {
          return true;
        }
        // Check if item has validation errors
        const errors = validateQuestion(item, t, availableGames);
        return Object.keys(errors).length > 0;
      });

      if (processedData.length > 0 && filteredData.length === 0) {
        snack.error(t('no_changes_detected') || 'No changes detected in the uploaded file.');
      }

      setNewQuestionList(filteredData);
      setHasUploadedFile(true);
    } catch (error) {
      console.error('Error processing CSV:', error);
    } finally {
      setIsLoadingData(false);
    }
  };

  const count = useMemo(() => {
    const statusCount = _.countBy(
      newQuestionList,
      (r) => r.status || 'existing'
    );

    // Count validation errors using our validation function with availableGames
    const validationErrors = newQuestionList.filter((question) => {
      const errors = validateQuestion(question, t, availableGames);
      return Object.keys(errors).length > 0;
    });

    if (validationErrors.length > 0) {
      statusCount.error = validationErrors.length;
    }

    return statusCount;
  }, [newQuestionList, t, availableGames]);

  // Check if there are any changes (new or update items)
  const hasChanges = useMemo(() => {
    return (count.new || 0) > 0 || (count.update || 0) > 0;
  }, [count]);

  return (
    <LayoutContainer title={t('import_questions')} backSrc="/admin/games">
      <Grid container spacing={1} mb={2}>
        <Grid
          item
          xs
          className="flex ac"
          sx={{ minWidth: 'min(100%,300px)', width: '200px' }}
        >
          <Grid container spacing={1}>
            <Grid item className="flex ac">
              <Box
                height="20px"
                width="20px"
                sx={{
                  backgroundColor: 'rgba(50, 255, 50,0.2) !important',
                  mr: 1,
                  border: '2px black solid'
                }}
              />
              <span>
                {' '}
                {t('add_questions')} ({count.new || 0})
              </span>
            </Grid>
            <Grid item className="flex ac">
              <Box
                height="20px"
                width="20px"
                sx={{
                  backgroundColor: 'rgba(255, 255, 50,0.2)!important',
                  mr: 1,
                  border: '2px black solid'
                }}
              />
              <span>
                {' '}
                {t('update_questions')} ({count.update || 0})
              </span>
            </Grid>
            <Grid item className="flex ac">
              <Box
                height="20px"
                width="20px"
                sx={{
                  backgroundColor: 'rgba(255, 50, 50,0.2) !important',
                  mr: 1,
                  border: '2px black solid'
                }}
              />
              <span>
                {' '}
                {t('invalid_data')} ({count.error || 0})
              </span>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Grid container spacing={1}>
            <Grid item className="flex ac">
              <Button onClick={handleExport} variant="outlined">
                {t('download_template')}
              </Button>
            </Grid>
            <Grid item className="flex ac">
              <Button 
                onClick={handleExportCurrentQuestions} 
                variant="outlined"
                disabled={existingQuestions.length === 0}
                startIcon={isLoadingSourceData ? <CircularProgress size={16} /> : null}
              >
                {t('download_current_questions') || 'Download Current Questions'}
              </Button>
            </Grid>
            <Grid item className="flex ac">
              <Button 
                onClick={() => utils.readFile(handleCsv, '.csv')}
              >
                {t('upload_update_file')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {sourceDataError && (
        <Box mb={2} p={2} sx={{ backgroundColor: '#ffebee', color: '#c62828', borderRadius: 1 }}>
          <Typography variant="body1">
            <strong>{t('error')}:</strong> {sourceDataError}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t('please_refresh_and_try_again') || 'Please refresh the page and try again.'}
          </Typography>
        </Box>
      )}
      <Box mb={2}>
        <Typography variant="h6" gutterBottom>
          {t('question_upload_guide_title')}
        </Typography>

        <Typography variant="subtitle2" component="div" sx={{ mb: 0.5 }}>
          {t('question_upload_step1_title')}
        </Typography>
        <Typography variant="body2" component="div" sx={{ mb: 1 }}>
          {t('question_upload_step1_desc')}
        </Typography>

        <Typography variant="subtitle2" component="div" sx={{ mb: 0.5 }}>
          {t('question_upload_step2_title')}
        </Typography>
        <Typography variant="body2" component="div" sx={{ mb: 1 }}>
          {t('question_upload_step2_desc')}
        </Typography>

        <Typography variant="subtitle2" component="div" sx={{ mb: 0.5 }}>
          {t('question_upload_step3_title')}
        </Typography>
        <Typography variant="body2" component="div" sx={{ mb: 1 }}>
          {t('question_upload_step3_desc')}
        </Typography>

        <Typography variant="subtitle2" component="div" sx={{ mb: 0.5 }}>
          {t('question_upload_step4_title')}
        </Typography>
        <Typography variant="body2" component="div" sx={{ mb: 1 }}>
          {t('question_upload_step4_desc')}
        </Typography>

        <Typography variant="subtitle2" component="div" sx={{ mt: 1, mb: 0.5 }}>
          {t('question_upload_note_title')}
        </Typography>
        <Typography variant="body2" component="div" sx={{ color: '#666' }}>
          • {t('question_upload_note1')}
          <br />• {t('question_upload_note2')}
        </Typography>
      </Box>
      <GameQuestionUploadTable
        validateQuestion={validateQuestion}
        rows={newQuestionList}
        existingQuestions={existingQuestions}
        availableGames={availableGames}
        _orderBy="index"
        _order="asc"
        loading={isLoadingData || (pendingCsvFile !== null)}
      />
      {hasUploadedFile && (
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button
            onClick={handleConfirm}
            disabled={count.error > 0 || !hasChanges}
          >
            {hasChanges ? t('save') : t('no_changes')}
          </Button>
        </Box>
      )}
      <EditDialog
        title={t('confirm_import_questions')}
        maxWidth="xs"
        confirmDisabled={formik.isSubmitting}
        handleSave={formik.handleSubmit}
        open={open}
        handleClose={() => setOpen(false)}
        confirmText={t('confirm')}
      >
        {count?.new > 0 && <p>{t('add_a_questions', { a: count?.new })}</p>}
        {count?.update > 0 && (
          <p>{t('update_a_questions', { a: count?.update })}</p>
        )}
      </EditDialog>
    </LayoutContainer>
  );
};

export default GameQuestionUpload;
